import { isCancel } from "axios";

import { reportApiError, reportApiWarning } from "api/api-server-actions";

function isRetryableNetworkError(error: unknown): boolean {
  return (
    error instanceof Error &&
    (error.message.includes("Network Error") ||
      error.message.includes("ERR_NETWORK") ||
      error.message.includes("timeout"))
  );
}

/** Transient transport failures or client-side aborts — log as warning, not error. */
function shouldReportFailureAsWarning(error: unknown): boolean {
  return (
    isRetryableNetworkError(error) ||
    isCancel(error) ||
    (error instanceof Error && error.message.includes("Request aborted"))
  );
}

/**
 * Retry function with exponential backoff for network errors
 */
export async function retryOnNetworkError<T>(
  operation: () => Promise<T>,
  operationName: string,
  maxRetries: number = 3,
): Promise<T> {
  const initialMaxRetries = maxRetries;

  async function retryWithBackoff<TInner>(
    op: () => Promise<TInner>,
    name: string,
    remainingRetries: number,
  ): Promise<TInner> {
    try {
      return await op();
    } catch (error) {
      if (isRetryableNetworkError(error) && remainingRetries > 1) {
        console.warn(
          `${name} failed with network error, retrying... (${remainingRetries - 1} attempts left)`,
        );
        // Wait a bit before retrying (exponential backoff)
        const attemptNumber = initialMaxRetries - remainingRetries + 1;
        await new Promise((resolve) => {
          setTimeout(resolve, 2 ** (attemptNumber - 1) * 1000);
        });
        return retryWithBackoff(op, name, remainingRetries - 1);
      }

      if (shouldReportFailureAsWarning(error)) {
        reportApiWarning(name, error);
      } else {
        reportApiError(name, error);
      }
      throw error;
    }
  }

  return retryWithBackoff(operation, operationName, maxRetries);
}
